import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiErrorMessage } from '@/lib/axios';

import {
  addProjectMember,
  completeDrawing,
  createPin,
  createProject,
  deleteDrawing,
  deletePin,
  deleteProject,
  fetchProjectStats,
  getDrawing,
  getProject,
  initDrawingUpload,
  listDrawings,
  listPages,
  listPins,
  listProjectMembers,
  listProjects,
  projectKeys,
  removeProjectMember,
  fetchProjectMemberIds,
  setProjectMembers,
  updatePin,
  updateProject,
  uploadBytes,
  type ProjectUpdateInput,
} from './api';
import type { Pin, ProjectCreateInput } from './types';

// ── Projects ──────────────────────────────────────────────────────────────
export function useProjects(params: { limit: number; offset: number }) {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: () => listProjects(params),
    placeholderData: (prev) => prev,
  });
}

export function useProjectStats() {
  return useQuery({
    queryKey: projectKeys.stats(),
    queryFn: fetchProjectStats,
    staleTime: 30_000,
  });
}

const memberKeys = {
  members: (projectId: string) => ['projects', projectId, 'members'] as const,
  memberIds: (projectId: string) => ['projects', projectId, 'member-ids'] as const,
};

export function useProjectMembers(projectId: string) {
  return useQuery({
    queryKey: memberKeys.members(projectId),
    queryFn: () => listProjectMembers(projectId),
    enabled: !!projectId,
  });
}

/** Ids of a project's current members — seeds the edit modal's team picker. */
export function useProjectMemberIds(projectId: string, enabled = true) {
  return useQuery({
    queryKey: memberKeys.memberIds(projectId),
    queryFn: () => fetchProjectMemberIds(projectId),
    enabled: enabled && !!projectId,
  });
}

/** Replace the project's whole member set (from the edit modal). */
export function useSetProjectMembers(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) => setProjectMembers(projectId, userIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: memberKeys.members(projectId) });
      void qc.invalidateQueries({ queryKey: memberKeys.memberIds(projectId) });
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to update team')),
  });
}

export function useAddProjectMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => addProjectMember(projectId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: memberKeys.members(projectId) });
      toast.success('User added to project');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to add member')),
  });
}

export function useRemoveProjectMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeProjectMember(projectId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: memberKeys.members(projectId) });
      toast.success('User removed from project');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to remove member')),
  });
}

export function useProject(id: string) {
  return useQuery({ queryKey: projectKeys.detail(id), queryFn: () => getProject(id), enabled: !!id });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectCreateInput) => createProject(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
      toast.success('Project created');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to create project')),
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectUpdateInput) => updateProject(id, input),
    onSuccess: (p) => {
      qc.setQueryData(projectKeys.detail(id), p);
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
      toast.success('Project updated');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to update project')),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
      toast.success('Project deleted');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete project')),
  });
}

// ── Drawings ──────────────────────────────────────────────────────────────
export function useDeleteDrawing(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (drawingId: string) => deleteDrawing(drawingId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.drawings(projectId) });
      toast.success('Drawing deleted');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete drawing')),
  });
}

export function useDrawings(projectId: string) {
  return useQuery({
    queryKey: projectKeys.drawings(projectId),
    queryFn: () => listDrawings(projectId),
    enabled: !!projectId,
  });
}

export function useDrawing(drawingId: string, poll = false) {
  return useQuery({
    queryKey: projectKeys.drawing(drawingId),
    queryFn: () => getDrawing(drawingId),
    enabled: !!drawingId,
    // Poll while the backend is still ingesting the drawing.
    refetchInterval: (q) => {
      const st = q.state.data?.drawing.status;
      return poll && st && st !== 'ready' && st !== 'failed' ? 2000 : false;
    },
  });
}

export function usePages(drawingId: string, enabled: boolean) {
  return useQuery({
    queryKey: projectKeys.pages(drawingId),
    queryFn: () => listPages(drawingId),
    enabled: enabled && !!drawingId,
  });
}

/** Full upload orchestration: init → PUT bytes → complete. */
export function useUploadDrawing(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      file: File;
      title: string;
      drawingNumber: string;
      stage: string;
      discipline: string;
      onProgress?: (pct: number) => void;
    }) => {
      const created = await initDrawingUpload(projectId, {
        title: args.title,
        drawing_number: args.drawingNumber,
        filename: args.file.name,
        content_type: args.file.type || 'application/octet-stream',
        stage: args.stage,
        discipline: args.discipline,
      });
      await uploadBytes(created.upload, args.file, args.onProgress);
      return completeDrawing(created.drawing.id, args.file.size);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.drawings(projectId) });
      toast.success('Uploaded — processing pages…');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Upload failed')),
  });
}

// ── Pins ──────────────────────────────────────────────────────────────────
export function usePins(drawingId: string, page: number) {
  return useQuery({
    queryKey: projectKeys.pins(drawingId, page),
    queryFn: () => listPins(drawingId, page),
    enabled: !!drawingId,
  });
}

export function useCreatePin(drawingId: string, page: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { page: number; x: number; y: number; label?: string; note?: string }) =>
      createPin(drawingId, body),
    onSuccess: (pin) => {
      qc.setQueryData<Pin[]>(projectKeys.pins(drawingId, page), (old) =>
        old ? [...old, pin] : [pin],
      );
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to add pin')),
  });
}

export function useUpdatePin(drawingId: string, page: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof updatePin>[1] }) =>
      updatePin(id, body),
    onSuccess: (pin) => {
      qc.setQueryData<Pin[]>(projectKeys.pins(drawingId, page), (old) =>
        old ? old.map((p) => (p.id === pin.id ? pin : p)) : [pin],
      );
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to update pin')),
  });
}

export function useDeletePin(drawingId: string, page: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePin(id),
    onSuccess: (_v, id) => {
      qc.setQueryData<Pin[]>(projectKeys.pins(drawingId, page), (old) =>
        old ? old.filter((p) => p.id !== id) : old,
      );
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete pin')),
  });
}
